import { useRef, useState } from 'react';

/**
 * Adds swipe-down-to-dismiss to a bottom sheet.
 * Apply `handlers` to the drag handle / header, `style` to the sheet panel.
 * Call `reset()` if the sheet re-opens without unmounting.
 */
export function useSwipeDown(onDismiss: () => void, threshold = 80) {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef<number | null>(null);

  const reset = () => { setDragY(0); setIsDragging(false); startY.current = null; };

  const onPointerDown = (e: React.PointerEvent) => {
    startY.current = e.clientY;
    setIsDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (startY.current === null) return;
    setDragY(Math.max(0, e.clientY - startY.current));
  };

  const onPointerUp = () => {
    const dy = dragY;
    setIsDragging(false);
    startY.current = null;
    if (dy > threshold) {
      onDismiss();
    } else {
      setDragY(0);
    }
  };

  const onPointerCancel = () => reset();

  const style: React.CSSProperties = {
    transform: `translateY(${dragY}px)`,
    transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.32,0.72,0,1)',
  };

  const handlers = { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };

  return { dragY, isDragging, style, handlers, reset };
}
